/**
 * Sort by match (TEC-744, spec 6.9): re-ranks an over-fetched window of search hits by the same
 * match score the cards show, then moves jobs down for what the user said they do not want.
 * It is a sort, never a filter: every hit that came in goes out, only the order changes.
 */
import type { DisplayJob } from '../../types';
import type { JobFormState } from '../../redux/features/jobFormSlice';
import type { ComparisonPreferencesState } from '../../redux/features/comparisonPreferencesSlice';
import type { SearchEntry } from '../../redux/features/savedSearchesSlice';
import { resolveJobMatchScore } from '../job-match/resolve-job-match-score';
import { matchAdjustmentFor, type MatchFeedbackState } from './match-feedback';

/** How many of the best-ranked search results are pulled in and re-ordered. */
export const MATCH_SORT_WINDOW = 100;

export interface RerankContext {
  userProfile: JobFormState;
  comparisonPreferences: ComparisonPreferencesState;
  savedSearches: SearchEntry[];
  feedback: MatchFeedbackState;
}

export interface RerankResult<T> {
  hits: T[];
  /** False when no match score could be computed for any hit (no profile, no search history). */
  scored: boolean;
}

/**
 * Stable: equal scores keep the order search gave them. Hits with no score of their own sit after
 * every scored hit, in search order, so a missing field never pushes a job to the top.
 */
export function rerankByMatch<T extends Record<string, unknown>>(hits: readonly T[], ctx: RerankContext): RerankResult<T> {
  let scoredAny = false;
  const rows = hits.map((hit, index) => {
    const resolved = resolveJobMatchScore({
      job: hit as unknown as DisplayJob,
      userProfile: ctx.userProfile,
      comparisonPreferences: ctx.comparisonPreferences,
      savedSearches: ctx.savedSearches,
      isHydrated: true,
    });
    const base = resolved.breakdown?.overall;
    if (typeof base === 'number') scoredAny = true;
    const adjusted = typeof base === 'number' ? base + matchAdjustmentFor(hit, ctx.feedback) : null;
    return { hit, index, adjusted };
  });
  if (!scoredAny) {
    // Nothing to match against: still honour what the user said they dislike, on top of search order.
    const nudged = rows.map((r) => ({ ...r, adjusted: -r.index * 0.01 + matchAdjustmentFor(r.hit, ctx.feedback) }));
    nudged.sort((a, b) => (b.adjusted as number) - (a.adjusted as number) || a.index - b.index);
    return { hits: nudged.map((r) => r.hit), scored: false };
  }
  rows.sort((a, b) => {
    if (a.adjusted === null && b.adjusted === null) return a.index - b.index;
    if (a.adjusted === null) return 1;
    if (b.adjusted === null) return -1;
    return b.adjusted - a.adjusted || a.index - b.index;
  });
  return { hits: rows.map((r) => r.hit), scored: true };
}

/**
 * Cuts one page out of a re-ranked window and fixes the paging numbers so pagination only walks
 * the window (the best-ranked MATCH_SORT_WINDOW results), which is what was actually re-ordered.
 */
export function sliceMatchWindow<R extends { hits?: unknown[]; nbHits?: number }>(
  windowResult: R,
  rankedHits: unknown[],
  page: number,
  perPage: number,
): R & { page: number; nbPages: number; hitsPerPage: number; matchWindow: number } {
  const total = rankedHits.length;
  const safePerPage = Math.max(1, perPage);
  const nbPages = Math.max(1, Math.ceil(total / safePerPage));
  const safePage = Math.min(Math.max(0, page), nbPages - 1);
  const start = safePage * safePerPage;
  return {
    ...windowResult,
    hits: rankedHits.slice(start, start + safePerPage),
    page: safePage,
    nbPages,
    hitsPerPage: safePerPage,
    matchWindow: total,
  };
}
